import React, { useState, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import {
  Box,
  Flex,
  Breadcrumb,
  Button,
  Tag,
  Radio,
  StatusLabel,
  Notification,
  Icon,
} from '@cowsquare/design';
import { Link, navigate } from 'gatsby';
import * as R from 'ramda';
import gql from 'graphql-tag';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Section } from '../../../Section';
import { Title } from '../../../Title';
import { BreadcrumbWrapper } from '../../../BreadcrumbWrapper';
import { Field } from '../../../MedicalContent';
import { mapIndexed } from '../../../../utils/ramdaExtension';
import { HTMLContent } from '../../../HTMLContent';
import { useUser } from '../../../../utils/hooks';

const SubmitButton = styled(Button).attrs({
  size: 'large',
  variant: 'primary',
})`
  width: 150px;
`;

const QuestionBox = styled(Box).attrs({
  mb: 8,
})`
  display: flex;
  flex-direction: column;
`;

const QuestionLabel = styled(Box).attrs({
  mb: 5,
})`
  display: table;
  font-size: 18px;
  line-height: 1.33;

  &::after {
    content: '';
    position: relative;
    display: table-cell;
    top: 50%;
    width: 100%;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    transform: translateY(50%);
  }
`;

const QuestionTitle = ({ children }) => (
  <QuestionLabel>
    <Box as="span" mr={4}>
      {children}
    </Box>
  </QuestionLabel>
);

const OptionLabel = styled.span`
  display: flex;
  align-items: center;
  ${props =>
    props.isEnd &&
    props.isCorrect &&
    css`
      color: ${themeGet('colors.primary')};
      ${Icon} {
        margin-right: 4px;
        color: ${themeGet('colors.success')};
      }
    `}
`;

const QuestionName = styled(Box).attrs({
  mb: 5,
})`
  font-size: 16px;
  line-height: 1.5;
`;

const OptionsGroup = props => (
  <Radio.Group itemsWrapperStyle={{ flexDirection: 'column', margin: 0 }} {...props} />
);

const genRadioOptions = (question, isEnd) =>
  mapIndexed((option, index) => {
    const isCorrect = question.answer === index;
    return {
      label: (
        <OptionLabel isEnd={isEnd} isCorrect={isCorrect}>
          {isEnd && isCorrect && <Icon name="SUCCESS" fontSize={20} />}
          <span>{`(${String.fromCharCode(65 + index)}) ${option}`}</span>
        </OptionLabel>
      ),
      value: `${index}`,
    };
  }, question.options);

const GET_CME_QUIZ_RECORD = gql`
  query getCMEQuizRecord($id: uuid!, $doctorId: uuid!) {
    record: hkdutest_cme_quizzes_safe(where: { id: { _eq: $id } }) {
      id
      name
      is_end
      is_publish
      published_at
      questions {
        id
        content
        name
        options
        answer_record(where: { doctor_id: { _eq: $doctorId } }) {
          question_id
          answer
        }
        answer
      }
      end_at
      point
      category {
        name
      }
    }
  }
`;

const UPSERT_CME_QUIZ_QUESTION_RECORDS = gql`
  mutation UpsertRecords($inputs: [hkdutest_cme_quiz_question_records_insert_input!]!) {
    records: insert_hkdutest_cme_quiz_question_records(
      objects: $inputs
      on_conflict: { constraint: cme_quiz_question_records_doctor_id_question_id_key, update_columns: answer }
    ) {
      returning {
        question_id
        answer
      }
    }
  }
`;

const convertAnsToUpsertInput = (doctorId, answers) =>
  R.map(R.merge(R.__, { doctor_id: doctorId, created_at: new Date() }), answers);

const isSubmitted = answerRecords => R.pipe(R.isEmpty, R.not)(answerRecords);

// compare lengths of questions and answers
const isAnswerCompleted = (questions, answers) => R.equals(R.length(questions), R.length(answers));
const getSelectedAnswer = (questionId, answers) =>
  R.pipe(
    R.find(R.propEq('question_id', questionId)),
    R.unless(R.isNil, record => `${R.prop('answer', record)}`)
  )(answers);

const QuizContent = ({ id }) => {
  const { HKDUUserInfo } = useUser();
  const [getCMEQuizRecord, { data, called }] = useLazyQuery(GET_CME_QUIZ_RECORD);
  const [upsertCMEQuizQuestionRecords, { loading: submitting }] = useMutation(
    UPSERT_CME_QUIZ_QUESTION_RECORDS
  );
  const [answers, setAnswers] = useState([]); // answers: [{questionId1: answer1}, {questionId2: answer2}, ...]
  const [submitted, setSubmitted] = useState(false);
  const doctorId = R.pathOr(null, ['doctor', 'id'], HKDUUserInfo);

  // prevent uneccessarily request with doctorId = null
  if (doctorId && !called) {
    getCMEQuizRecord({ variables: { id, doctorId } });
  }

  const record = R.pathOr({}, ['record', '0'], data);
  const title = R.propOr('', 'name', record);
  const category = R.pathOr('', ['category', 'name'], record);
  const questions = R.propOr([], 'questions', record);
  const answerRecords = R.pipe(R.pluck('answer_record'), R.flatten, R.map(R.dissoc('__typename')))(questions);
  const isEnd = R.propOr(false, 'is_end', record);

  useEffect(() => {
    // 尚未作答，且有作答記錄時才要執行
    if (R.isEmpty(answers) && !R.isEmpty(answerRecords)) {
      setAnswers(answerRecords);

      if (isSubmitted(answerRecords)) {
        setSubmitted(true);
      }
    }
  }, [answerRecords, answers]);

  const onSelectOption = questionId => answer =>
    setAnswers(answers =>
      R.pipe(
        R.findIndex(R.propEq('question_id', questionId)),
        R.ifElse(R.equals(-1), R.always(R.append({ question_id: questionId, answer }, answers)), index =>
          R.over(R.lensIndex(index), R.assoc('answer', answer), answers)
        )
      )(answers)
    );

  const onSubmit = useCallback(() => {
    const inputs = convertAnsToUpsertInput(doctorId, answers);
    upsertCMEQuizQuestionRecords({ variables: { inputs } })
      .then(() => {
        setSubmitted(true);
        if (submitted) {
          Notification.success({
            message: 'Updated!',
            description: 'The answers have been submitted again.',
          });
        } else {
          Notification.success({
            message: 'Success!',
            description: 'The quiz has been submitted.',
          });
        }
      })
      .catch(() => {
        Notification.error({
          message: 'Failed!',
          description: 'Unable to submit this quiz, please try again.',
        });
      });
  }, [answers, doctorId, submitted, upsertCMEQuizQuestionRecords]);

  return (
    <Section>
      <BreadcrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Item linkProps={{ as: Link, to: '/cme/quizzes' }}>Online Quizzes</Breadcrumb.Item>
          <Breadcrumb.Item linkProps={{ as: Link, to: `/cme/quizzes?category=${category}` }}>
            {category}
          </Breadcrumb.Item>
          <Breadcrumb.Item>{title}</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbWrapper>
      <Box px={[5, 0]} py={8}>
        {submitted && (
          <Box mb={5}>
            <StatusLabel type="success">Submitted</StatusLabel>
          </Box>
        )}
        <Title mb={5}>{title}</Title>
        {category && (
          <Tag.Group mb={6}>
            <Tag
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                navigate(`/cme/quizzes?category=${category}`);
              }}
            >
              {category}
            </Tag>
          </Tag.Group>
        )}
        <Box mb={8}>
          <Field label="CME Point" value={record.point} />
          <Field label="End Date" value={record.end_at} />
        </Box>
        <Flex flexDirection="column">
          {mapIndexed(
            (question, index) => (
              <QuestionBox key={question.id}>
                <QuestionTitle>{`Q${index + 1}`}</QuestionTitle>
                <Box mb={7}>
                  <HTMLContent html={question.content} />
                </Box>
                <QuestionName>{question.name}</QuestionName>
                <OptionsGroup
                  value={getSelectedAnswer(question.id, answers)}
                  disabled={isEnd || submitting}
                  options={genRadioOptions(question, isEnd)}
                  onChange={onSelectOption(question.id)}
                />
              </QuestionBox>
            ),
            questions
          )}
        </Flex>
      </Box>
      {R.length(questions) > 0 && !isEnd && (
        <Box mb={8}>
          <Flex justifyContent="center">
            <SubmitButton disabled={submitting || !isAnswerCompleted(questions, answers)} onClick={onSubmit}>
              {submitted ? 'Update Answer' : 'Submit'}
            </SubmitButton>
          </Flex>
        </Box>
      )}
    </Section>
  );
};

export default QuizContent;
